import { forwardRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';
import { PIN_SVG, RoadSign, type SignName } from './signs';

export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { raised?: boolean }>(function Card(
  { raised = false, className, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('min-w-0 rounded-lg border border-line', raised ? 'bg-surface-raised shadow-2' : 'bg-surface shadow-1', className)}
      {...rest}
    />
  );
});

export function CardHeader({ title, description, actions, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-line px-4 py-3', className)}>
      <div className="min-w-0">
        <h2 className="text-title text-ink">{title}</h2>
        {description && <p className="text-body-sm text-ink-subtle">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** A page's title row: the title in the display face, one line of context, the page's actions at the end. */
export function PageHeader({ title, description, actions, back, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; back?: ReactNode; className?: string }) {
  return (
    <header className={cn('flex flex-wrap items-end justify-between gap-x-6 gap-y-3', className)}>
      <div className="grid min-w-0 gap-1">
        {back}
        <h1 className="font-display text-h1 text-ink">{title}</h1>
        {description && <p className="text-body text-ink-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export interface KpiTileProps {
  label: ReactNode;
  sign?: SignName;
  icon?: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  /** 0–1: the share of the fleet, drawn as a go meter */
  meter?: number;
  foot?: ReactNode;
  delta?: ReactNode;
  /** An alarm tile: a 3 px hazard rail on its start edge. */
  hazard?: boolean;
  className?: string;
  onClick?: () => void;
  pressed?: boolean;
}

/** sign-style label with its sign, the metric value (+ unit), an optional meter and a foot line. */
export function KpiTile({ label, sign, icon, value, unit, meter, foot, delta, hazard = false, className, onClick, pressed }: KpiTileProps) {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-pressed={onClick ? pressed : undefined}
      className={cn(
        'relative grid min-w-0 content-start gap-2.5 overflow-hidden rounded-lg border bg-surface px-[18px] pb-4 pt-[18px] text-start shadow-1',
        pressed ? 'border-selected-line' : 'border-line',
        onClick && 'transition-colors duration-quick hover:border-line-input',
        hazard && 'before:absolute before:inset-y-0 before:start-0 before:w-[3px] before:bg-hazard',
        className,
      )}
    >
      <p className="flex items-center gap-2 text-sign uppercase text-ink-subtle">
        {sign ? <RoadSign sign={sign} /> : icon}
        {label}
      </p>
      <div className="flex min-w-0 items-baseline gap-2">
        <span className="font-mono text-metric text-ink">{value}</span>
        {unit && <small className="font-mono text-[14px] font-semibold text-ink-subtle">{unit}</small>}
      </div>
      {meter !== undefined && (
        <div className="h-1.5 overflow-hidden rounded-[3px] border border-line bg-surface-sunken" aria-hidden="true">
          <i className="block h-full bg-go transition-[width] duration-move ease-drive" style={{ width: `${Math.round(Math.min(1, Math.max(0, meter)) * 100)}%` }} />
        </div>
      )}
      {(foot || delta) && (
        <div className="flex items-center justify-between gap-2 text-[12px] text-ink-subtle">
          <span className="min-w-0 truncate">{foot}</span>
          {delta}
        </div>
      )}
    </Comp>
  );
}

/** A short road ending at a pin, one plain sentence, one action. */
export function EmptyState({ title, body, action, className }: { title: ReactNode; body?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('grid justify-items-center gap-3 px-[22px] py-7 text-center', className)}>
      <svg viewBox="0 0 160 90" width="160" height="90" className="rtl:-scale-x-100" aria-hidden="true" focusable="false">
        <path d="M8 80C50 80 60 54 80 46S118 34 128 22" fill="none" style={{ stroke: 'var(--vlm-line-strong)' }} strokeWidth="10" strokeLinecap="round" />
        <path d="M8 80C50 80 60 54 80 46S118 34 128 22" fill="none" style={{ stroke: 'var(--vlm-surface)' }} strokeWidth="2" strokeDasharray="7 5" />
        <svg x="116" y="-4" width="24" height="32" viewBox="0 0 32 42" dangerouslySetInnerHTML={{ __html: PIN_SVG }} />
      </svg>
      <b className="text-title text-ink">{title}</b>
      {body && <p className="max-w-[38ch] text-body-sm text-ink-subtle">{body}</p>}
      {action}
    </div>
  );
}

/** Loading: the lane flowing in red on a sunken track, toward the reading end. */
export function LaneProgress({ label, thin = false, className }: { label: string; thin?: boolean; className?: string }) {
  return (
    <div role="progressbar" aria-label={label} className={cn('vlm-lane-progress', thin && 'is-thin', className)}>
      <i />
    </div>
  );
}

/** A placeholder block. It does not shimmer: content replaces it within one cycle or the lane says why. */
export function Skeleton({ className }: { className?: string }) {
  return <span className={cn('block h-3.5 rounded-xs bg-surface-sunken', className)} aria-hidden="true" />;
}

export function Note({ sign = 'info', children, className }: { sign?: SignName; children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-start gap-2.5 rounded-md border border-line bg-surface-sunken px-4 py-3.5 text-body-sm text-ink-muted', className)}>
      <RoadSign sign={sign} size={20} className="mt-px" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** The machine voice — plates, IMEIs, coordinates, units, timestamps — kept left-to-right inside Arabic. */
export function Ltr({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <bdi dir="ltr" className={className}>
      {children}
    </bdi>
  );
}

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => Array.from(w)[0] ?? '')
    .join('')
    .toUpperCase();
}

/** A person: their photo, or their initials on the action colour. */
export function Avatar({ name, src, size = 32, className }: { name: string; src?: string | null; size?: number; className?: string }) {
  const [failed, setFailed] = useState<string | null>(null);
  if (src && failed !== src) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        onError={() => setFailed(src)}
        className={cn('shrink-0 rounded-full border border-line object-cover', className)}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className={cn('grid shrink-0 place-items-center rounded-full bg-action font-bold text-on-action', className)}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.36)) }}
    >
      {initialsOf(name)}
    </span>
  );
}
